import React, { useCallback, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get } from '@/lib/api';
import { softDeleteMany } from '@/lib/masterDataValidation';
import { PageHeader } from '@/components/PageHeader';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { RolesSection } from '@/components/settings/roles/RolesSection';
import { DepartmentsSection } from '@/components/settings/roles/DepartmentsSection';
import { WorkCategoriesDialog } from '@/components/settings/roles/WorkCategoriesDialog';
import { toast } from 'sonner';

export default function RolesSettings() {
  const qc = useQueryClient();
  const { data: roles = [], isLoading: rolesLoading, isError: rolesError } = useQuery({
    queryKey: ['v1', 'roles'],
    queryFn: () => v1Get('/master/roles'),
  });
  const { data: depts = [], isLoading: deptsLoading, isError: deptsError } = useQuery({
    queryKey: ['v1', 'departments'],
    queryFn: () => v1Get('/master/departments'),
  });

  const roleClearRef = useRef(null);
  const deptSectionRef = useRef(null);

  const [catOpen, setCatOpen] = useState(null);
  const [catInitKey, setCatInitKey] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const registerRoleClearSelection = useCallback((fn) => {
    roleClearRef.current = fn;
  }, []);

  const handleManageCategories = useCallback((d) => {
    setCatInitKey((k) => k + 1);
    setCatOpen(d);
  }, []);

  const confirmDelete = async () => {
    if (!deleteConfirm || deleting) return;
    setDeleting(true);
    const { kind, ids } = deleteConfirm;
    const apiPath = kind === 'role' ? '/master/roles' : '/master/departments';
    const noun = kind === 'role' ? 'role' : 'department';
    try {
      const { ok, failed } = await softDeleteMany(apiPath, ids);
      if (kind === 'role') {
        roleClearRef.current?.();
        qc.invalidateQueries({ queryKey: ['v1', 'roles'] });
      } else {
        deptSectionRef.current?.clearSelection();
        deptSectionRef.current?.closeSubIfDeleted(ids);
        qc.invalidateQueries({ queryKey: ['v1', 'departments'] });
      }
      if (failed === 0) {
        toast.success(ok === 1 ? `${noun[0].toUpperCase()}${noun.slice(1)} deleted.` : `${ok} ${noun}s deleted.`);
      } else {
        toast.error(`Deleted ${ok}, failed ${failed}.`);
      }
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-3rem)] flex-col gap-6 md:h-[calc(100vh-4rem)]" data-testid="roles-departments-page">
      <PageHeader
        className="shrink-0"
        title="Roles & Departments"
        subtitle="Master lists used across candidate creation, question banks, and pipeline configuration."
      />
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-2">
        <RolesSection
          roles={roles}
          isLoading={rolesLoading}
          isError={rolesError}
          onRequestDelete={setDeleteConfirm}
          onRegisterClearSelection={registerRoleClearSelection}
        />
        <DepartmentsSection
          ref={deptSectionRef}
          depts={depts}
          isLoading={deptsLoading}
          isError={deptsError}
          onRequestDelete={setDeleteConfirm}
          onManageCategories={handleManageCategories}
        />
      </div>

      <WorkCategoriesDialog
        dept={catOpen}
        initKey={catInitKey}
        onClose={() => setCatOpen(null)}
        onOpenDept={setCatOpen}
      />

      <AlertDialog open={!!deleteConfirm} onOpenChange={(v) => { if (!deleting && !v) setDeleteConfirm(null); }}>
        <AlertDialogContent data-testid="master-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteConfirm && deleteConfirm.ids.length > 1
                ? `Delete ${deleteConfirm.ids.length} ${deleteConfirm.kind === 'role' ? 'roles' : 'departments'}?`
                : `Delete this ${deleteConfirm?.kind === 'role' ? 'role' : 'department'}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm?.label}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="master-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="master-delete-confirm-action"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
